/**
 * Hook to manage multiple independent animations for a slide.
 * Creates and coordinates animation instances for each play block.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { SlideContentBlockWithPlay, SlideWithContent } from '@/db/Types'
import type { Drawing, Player } from '@/types'
import { type AnimationInstance, useAnimationInstance } from './UseAnimationInstance'
import { validateDrawings, validatePlayers } from '@/utils/TypeGuards'

type PlayContent = {
	id: string
	players: Player[]
	drawings: Drawing[]
}

type SlideAnimationsOptions = {
	/** The slide with content blocks */
	slide: SlideWithContent | null
	/** Whether to auto-play animations when slide loads */
	autoPlay?: boolean
	/** Whether to loop animations */
	loop?: boolean
	/** Callback when all animations complete */
	onAllComplete?: () => void
}

type SlideAnimations = {
	/** Map of block ID to animation instance */
	animations: Map<string, AnimationInstance>
	/** Get animation instance for a specific block ID */
	getAnimationForBlock: (blockId: string) => AnimationInstance | null
	/** Play all animations */
	playAll: () => void
	/** Pause all animations */
	pauseAll: () => void
	/** Reset all animations */
	resetAll: () => void
	/** Toggle all animations (play if any paused, pause if all playing) */
	toggleAll: () => void
	/** Whether any animation is currently playing */
	isAnyPlaying: boolean
	/** Whether all animations are complete */
	areAllComplete: boolean
	/** Number of play blocks on this slide */
	playBlockCount: number
}

/**
 * Creates a stub animation instance for blocks without play data.
 * This ensures consistent Map structure even for unloaded blocks.
 */
function createStubInstance(): AnimationInstance {
	const stubState = {
		phase: 'ready' as const,
		isPlaying: false,
		currentTime: 0,
		totalDuration: 0,
		playbackSpeed: 1.5 as const,
		preSnapDuration: 0,
		playId: null,
		playerStates: [],
		showGhostTrail: false,
		loopMode: false,
		hasBeenTriggered: false,
		routeTimings: new Map(),
	}

	return {
		state: stubState,
		play: () => {
		},
		pause: () => {
		},
		reset: () => {
		},
		toggle: () => {
		},
		seek: () => {
		},
		setSpeed: () => {
		},
		toggleLoop: () => {
		},
		toggleGhostTrail: () => {
		},
		isReady: false,
		isPlaying: false,
		isComplete: false,
		progress: 0,
	}
}

/**
 * Internal hook that creates one animation instance per content block.
 * Named to avoid confusion with useBlockAnimation from SlideAnimationsContext.
 */
function useBlockAnimationSlot(
	block: SlideContentBlockWithPlay | null,
	autoPlay: boolean,
	loop: boolean,
): AnimationInstance {
	// Extract play data from block with runtime validation
	const playData = useMemo(() => {
		if (!block?.play) return null

		return {
			id: block.play.id.toString(),
			players: validatePlayers(block.play.custom_players),
			drawings: validateDrawings(block.play.custom_drawings),
		}
	}, [block?.play])

	return useAnimationInstance({
		playData,
		autoPlay,
		loop,
	})
}

/**
 * Maximum play blocks supported per slide.
 *
 * @limitation Due to React's hooks rules (hooks cannot be called conditionally
 * or in loops), we must create a fixed number of animation slots. Slides with
 * more than this many play blocks will only animate the first MAX_PLAY_BLOCKS.
 *
 * This number was chosen as a practical maximum:
 * - Most slides have 1-2 play blocks
 * - Side-by-side layouts use 2 plays
 * - Maximum practical grid rarely exceeds 4
 *
 * If you need more, consider splitting across multiple slides.
 */
const MAX_PLAY_BLOCKS = 8

export function useSlideAnimations({
									   slide,
									   autoPlay = false,
									   loop = true,
									   onAllComplete,
								   }: SlideAnimationsOptions): SlideAnimations {
	// Extract play blocks from slide
	const playBlocks = useMemo(() => {
		if (!slide) return []
		return slide.content_blocks.filter((block) => block.type === 'play')
	}, [slide])

	// Get block at index or null (for fixed hook calls)
	const getBlock = (index: number): SlideContentBlockWithPlay | null => {
		return playBlocks[index] ?? null
	}

	// Create animation instances for each slot (must be called unconditionally)
	const anim0 = useBlockAnimationSlot(getBlock(0), autoPlay, loop)
	const anim1 = useBlockAnimationSlot(getBlock(1), autoPlay, loop)
	const anim2 = useBlockAnimationSlot(getBlock(2), autoPlay, loop)
	const anim3 = useBlockAnimationSlot(getBlock(3), autoPlay, loop)
	const anim4 = useBlockAnimationSlot(getBlock(4), autoPlay, loop)
	const anim5 = useBlockAnimationSlot(getBlock(5), autoPlay, loop)
	const anim6 = useBlockAnimationSlot(getBlock(6), autoPlay, loop)
	const anim7 = useBlockAnimationSlot(getBlock(7), autoPlay, loop)

	// Keep animations in a ref to avoid recreating callbacks on every render
	// The individual instances are stable, only their internal state changes
	const allAnimationsRef = useRef<AnimationInstance[]>([anim0, anim1, anim2, anim3, anim4, anim5, anim6, anim7])
	allAnimationsRef.current = [anim0, anim1, anim2, anim3, anim4, anim5, anim6, anim7]

	// Build map of block ID to animation instance
	// Note: This map is recreated when playBlocks change, but the instances inside are stable
	const animations = useMemo(() => {
		const map = new Map<string, AnimationInstance>()
		playBlocks.forEach((block, index) => {
			if (index < MAX_PLAY_BLOCKS) {
				const allAnims = allAnimationsRef.current
				const anim = allAnims[index]
				if (anim) {
					map.set(block.id, anim)
				}
			}
		})
		return map
		// Only recreate when playBlocks structure changes (block IDs change)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [playBlocks.map((b) => b.id).join(',')])

	// Track completion
	const onAllCompleteRef = useRef(onAllComplete)
	useEffect(() => {
		onAllCompleteRef.current = onAllComplete
	}, [onAllComplete])

	// Computed values - read from individual animation states directly
	// These need to update when any animation state changes
	const isAnyPlaying = anim0.isPlaying || anim1.isPlaying || anim2.isPlaying || anim3.isPlaying ||
		anim4.isPlaying || anim5.isPlaying || anim6.isPlaying || anim7.isPlaying

	const activeCount = Math.min(playBlocks.length, MAX_PLAY_BLOCKS)
	const areAllComplete = activeCount > 0 && [anim0, anim1, anim2, anim3, anim4, anim5, anim6, anim7]
		.slice(0, activeCount)
		.every((anim) => anim.isComplete || !anim.isReady)

	// Control methods - use refs to avoid recreating callbacks
	const playAll = useCallback(() => {
		const count = Math.min(playBlocks.length, MAX_PLAY_BLOCKS)
		const anims = allAnimationsRef.current
		for (let i = 0; i < count; i++) {
			anims[i]?.play()
		}
	}, [playBlocks.length])

	const pauseAll = useCallback(() => {
		const count = Math.min(playBlocks.length, MAX_PLAY_BLOCKS)
		const anims = allAnimationsRef.current
		for (let i = 0; i < count; i++) {
			anims[i]?.pause()
		}
	}, [playBlocks.length])

	const resetAll = useCallback(() => {
		const count = Math.min(playBlocks.length, MAX_PLAY_BLOCKS)
		const anims = allAnimationsRef.current
		for (let i = 0; i < count; i++) {
			anims[i]?.reset()
		}
	}, [playBlocks.length])

	const toggleAll = useCallback(() => {
		if (isAnyPlaying) {
			pauseAll()
		} else {
			playAll()
		}
	}, [isAnyPlaying, playAll, pauseAll])

	// Fire onAllComplete when all animations finish
	useEffect(() => {
		if (areAllComplete && playBlocks.length > 0) {
			onAllCompleteRef.current?.()
		}
	}, [areAllComplete, playBlocks.length])

	// Get animation for a specific block
	const getAnimationForBlock = useCallback(
		(blockId: string): AnimationInstance | null => {
			return animations.get(blockId) ?? null
		},
		[animations],
	)

	return {
		animations,
		getAnimationForBlock,
		playAll,
		pauseAll,
		resetAll,
		toggleAll,
		isAnyPlaying,
		areAllComplete,
		playBlockCount: playBlocks.length,
	}
}

export type { SlideAnimations, PlayContent }
